import React, { useState, useRef, useEffect } from 'react';
import { registryAPI } from '../services/api';

// Curated image catalogue grouped by category
export const IMAGE_CATALOGUE = [
  {
    group: '🖥 System / Base',
    images: [
      { name: 'ubuntu:22.04',      desc: 'Ubuntu 22.04 LTS — most compatible' },
      { name: 'ubuntu:24.04',      desc: 'Ubuntu 24.04 LTS — latest' },
      { name: 'debian:12-slim',    desc: 'Debian 12 Bookworm slim' },
      { name: 'alpine:3.19',       desc: 'Alpine Linux — smallest base (5MB)' },
    ],
  },
  {
    group: '🐍 Python',
    images: [
      { name: 'python:3.11-slim',  desc: 'Python 3.11 slim — recommended' },
      { name: 'python:3.12-slim',  desc: 'Python 3.12 slim — latest' },
      { name: 'python:3.10-slim',  desc: 'Python 3.10 slim' },
    ],
  },
  {
    group: '🟢 Node.js',
    images: [
      { name: 'node:20-alpine',    desc: 'Node.js 20 LTS Alpine — small' },
      { name: 'node:18-alpine',    desc: 'Node.js 18 LTS Alpine' },
      { name: 'node:20-slim',      desc: 'Node.js 20 LTS Debian slim' },
    ],
  },
  {
    group: '☕ Java / JVM',
    images: [
      { name: 'openjdk:21-slim',   desc: 'OpenJDK 21 LTS slim' },
      { name: 'openjdk:17-slim',   desc: 'OpenJDK 17 LTS slim' },
      { name: 'eclipse-temurin:21-jre-alpine', desc: 'Temurin JRE 21 Alpine' },
    ],
  },
  {
    group: '🐘 Databases',
    images: [
      { name: 'postgres:16',       desc: 'PostgreSQL 16' },
      { name: 'postgres:15',       desc: 'PostgreSQL 15' },
      { name: 'redis:7.2-alpine',      desc: 'Redis 7.2 Alpine (official, amd64+arm64)' },
      { name: '11220/redis:7.2-alpine', desc: 'Redis 7.2 (I4C DockerHub mirror, amd64)' },
      { name: 'mongo:7',           desc: 'MongoDB 7' },
      { name: 'mysql:8.0',         desc: 'MySQL 8.0' },
      { name: 'mariadb:11',        desc: 'MariaDB 11' },
    ],
  },
  {
    group: '🌐 Web Servers',
    images: [
      { name: 'nginx:alpine',      desc: 'Nginx Alpine — web server / proxy' },
      { name: 'caddy:2-alpine',    desc: 'Caddy 2 — auto HTTPS' },
    ],
  },
  {
    group: '📦 Messaging / Queue',
    images: [
      { name: 'rabbitmq:3.13-management-alpine', desc: 'RabbitMQ 3.13 with Management UI (official, amd64+arm64)' },
      { name: '11220/rabbitmq:3.13-management',   desc: 'RabbitMQ 3.13 (I4C DockerHub mirror, amd64)' },
      { name: 'apache/kafka:3.7.0', desc: 'Apache Kafka 3.7' },
    ],
  },
  {
    group: '🔍 Search / Analytics',
    images: [
      { name: 'docker.elastic.co/elasticsearch/elasticsearch:8.13.0', desc: 'Elasticsearch 8.13 (official amd64+arm64)' },
      { name: 'docker.elastic.co/kibana/kibana:8.13.0',               desc: 'Kibana 8.13 (official amd64+arm64)' },
      { name: '11220/elasticsearch:8.13.0',                            desc: 'Elasticsearch 8.13 (I4C DockerHub mirror, amd64)' },
      { name: '11220/kibana:8.13.0',                                   desc: 'Kibana 8.13 (I4C DockerHub mirror, amd64)' },
    ],
  },
  {
    group: '💾 Object Storage',
    images: [
      { name: 'minio/minio:RELEASE.2024-01-16T16-07-38Z', desc: 'MinIO object storage (official, amd64+arm64)' },
      { name: '11220/minio:latest',                        desc: 'MinIO (I4C DockerHub mirror, amd64)' },
    ],
  },
  {
    group: '⚙ Other',
    images: [
      { name: 'golang:1.22-alpine',  desc: 'Go 1.22 Alpine' },
      { name: 'php:8.3-apache',      desc: 'PHP 8.3 with Apache' },
      { name: 'ruby:3.3-alpine',     desc: 'Ruby 3.3 Alpine' },
      { name: 'rust:1-slim',         desc: 'Rust latest slim' },
    ],
  },
];

const ALL_IMAGES = IMAGE_CATALOGUE.flatMap(g => g.images.map(i => ({ ...i, group: g.group })));

// Label for the dynamic group of images pulled from the internal registry.
const REGISTRY_GROUP = '📦 My Registry';

export default function ImageSelector({ value, onChange, placeholder, required }) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState('');
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState('');
  // Images from the internal container registry (Phase 1) — a dynamic group merged
  // with the static catalogue. Fails soft: if unreachable, just show the catalogue.
  const [registryImages, setRegistryImages] = useState([]); // [{name, desc, group}]
  const ref = useRef();

  // Fetch registry images once on mount. Registry base is resolved server-side.
  useEffect(() => {
    let cancelled = false;
    registryAPI.listImages()
      .then(r => {
        if (cancelled) return;
        // The selected value MUST be the FULL pullable ref including the registry
        // host (e.g. 103.255.103.45:32478/test:v3), NOT a bare repo:tag. A bare
        // "test:v3" is interpreted by Kubernetes as a Docker Hub image
        // (docker.io/library/test:v3) which doesn't exist → the pod never runs
        // your built image (the "no package after restart" bug). Prepend the
        // registry host (scheme stripped) from the API's registry_url.
        const host = (r.data?.registry_url || '').replace(/^https?:\/\//, '').replace(/\/$/, '');
        const imgs = [];
        for (const repo of (r.data?.images || [])) {
          for (const tag of (repo.tags || [])) {
            const ref = host ? `${host}/${repo.repository}:${tag}` : `${repo.repository}:${tag}`;
            imgs.push({ name: ref, desc: 'Internal registry image', group: REGISTRY_GROUP });
          }
        }
        setRegistryImages(imgs);
      })
      .catch(() => { /* registry unreachable — catalogue still works */ });
    return () => { cancelled = true; };
  }, []);

  // Combined searchable list = registry images (first) + static catalogue.
  const allImages = [...registryImages, ...ALL_IMAGES];

  // Determine if current value is from catalogue/registry or custom
  useEffect(() => {
    const known = allImages.some(i => i.name === value);
    if (value && !known) {
      setCustom(true);
      setCustomVal(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registryImages]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search.trim()
    ? allImages.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        i.desc.toLowerCase().includes(search.toLowerCase())
      )
    : null;

  const handleSelect = (imgName) => {
    onChange(imgName);
    setCustom(false);
    setOpen(false);
    setSearch('');
  };

  const handleCustom = () => {
    setCustom(true);
    setOpen(false);
    setSearch('');
    onChange(customVal);
  };

  const displayValue = custom ? customVal : (value || '');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Main trigger */}
      {!custom ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            readOnly
            className="input"
            value={value || ''}
            placeholder={placeholder || 'Select or type an image...'}
            onClick={() => setOpen(o => !o)}
            style={{ flex: 1, cursor: 'pointer', caretColor: 'transparent' }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(o => !o)}>
            {open ? '▲' : '▼'}
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleCustom}
            title="Type a custom image name">
            ✏ Custom
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            autoFocus
            className="input"
            value={customVal}
            onChange={e => { setCustomVal(e.target.value); onChange(e.target.value); }}
            placeholder="e.g. myorg/myapp:v1.0"
            style={{ flex: 1 }}
          />
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setCustom(false); onChange(''); setCustomVal(''); }}
            title="Back to catalogue">
            📋 List
          </button>
        </div>
      )}

      {/* Selected image tag */}
      {value && !custom && (
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
          <code style={{ fontSize: 11, background: 'var(--brand-soft)', color: 'var(--brand)', padding: '2px 8px', borderRadius: 4 }}>{value}</code>
          <span style={{ fontSize: 11, color: 'var(--muted-2)' }}>
            {allImages.find(i => i.name === value)?.desc || ''}
          </span>
          <button type="button" onClick={() => { onChange(''); }}
            style={{ fontSize: 10, color: 'var(--color-danger-500)', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 4,
          maxHeight: 380, overflowY: 'auto',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--surface)' }}>
            <input
              autoFocus
              className="input"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search images..."
              onClick={e => e.stopPropagation()}
            />
          </div>

          {/* Results */}
          {filtered ? (
            filtered.length > 0 ? (
              <div>
                {filtered.map(img => (
                  <div key={img.name} onClick={() => handleSelect(img.name)}
                    style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2,
                      background: value === img.name ? 'var(--brand-soft)' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = value === img.name ? 'var(--brand-soft)' : 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600 }}>{img.name}</code>
                      <span style={{ fontSize: 10, color: 'var(--muted)', background: 'var(--surface-2)', padding: '1px 6px', borderRadius: 8 }}>{img.group}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)' }}>{img.desc}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--muted-2)', fontSize: 13 }}>
                No matching images.
                <button type="button" onClick={handleCustom}
                  style={{ display: 'block', margin: '8px auto 0', color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>
                  ✏ Use "{search}" as custom image
                </button>
              </div>
            )
          ) : (
            /* Browse by category — registry group first (if any), then catalogue */
            [
              ...(registryImages.length ? [{ group: REGISTRY_GROUP, images: registryImages }] : []),
              ...IMAGE_CATALOGUE,
            ].map(group => (
              <div key={group.group}>
                <div style={{ padding: '6px 14px 4px', fontSize: 11, fontWeight: 700, color: 'var(--muted)',
                  background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', position: 'sticky', top: 51, zIndex: 1 }}>
                  {group.group}
                </div>
                {group.images.map(img => (
                  <div key={img.name} onClick={() => handleSelect(img.name)}
                    style={{ padding: '8px 14px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 2,
                      background: value === img.name ? 'var(--brand-soft)' : 'transparent' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = value === img.name ? 'var(--brand-soft)' : 'transparent'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <code style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: value===img.name ? 700 : 500 }}>{img.name}</code>
                      {value === img.name && <span style={{ fontSize: 10, color: 'var(--color-success-500)' }}>✓ selected</span>}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--muted-2)' }}>{img.desc}</div>
                  </div>
                ))}
              </div>
            ))
          )}

          {/* Custom option at bottom */}
          <div style={{ borderTop: '1px solid var(--border)', padding: '10px 14px', background: 'var(--surface-2)' }}>
            <button type="button" onClick={handleCustom}
              style={{ color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              ✏ Type a custom image name
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
